import axios from 'axios';
import getWebsocketClient from '@bynder/js-websocket-client-lib';
import { API_ROUTES, CONFIG, isDevelopment } from '../../constants/app';
import { csrf } from './uploadApi';
import {
  CollectionFeedResponse,
  NormalizedFeedCollectionResponse,
} from '../../types/widgets/collections';
import { getWSEndpoint } from '../../constants/sockets';

const WS_ENTITY_NAME = 'files-messages';
const WS_DOWNLOAD_EVENT_NAME = 'MultiDownloadAccessible';

export const waitForCollectionDownloadAvailable = async (
  downloadId: string,
) => {
  if (isDevelopment) {
    return Promise.resolve();
  }

  const webSocketClient = await getWebsocketClient(
    CONFIG.SOCKET_SERVER_URL,
    getWSEndpoint(),
    undefined,
    false,
  );

  await webSocketClient.joinRoom(`${WS_ENTITY_NAME}_${downloadId}`);
  await webSocketClient.promisifySubscribe(
    WS_ENTITY_NAME,
    downloadId,
    WS_DOWNLOAD_EVENT_NAME,
  );
};

export const downloadCollection = (
  collectionId: string,
  data: NormalizedFeedCollectionResponse,
) => {
  const params = new URLSearchParams();
  params.append('type', 'selection');
  params.append('iframe', 'false');
  params.append('includeArchive', 'true');
  params.append('collectionId', collectionId);
  params.append('addAdditionals', 'false');
  params.append('addWebversions', '@original');
  const assetIds = getAllAssetIdsFromFeedsCollection(data);
  assetIds.forEach((asset: string) => {
    params.append('media_ids[]', asset);
  });

  return axios.post(API_ROUTES.ASSET_DOWNLOAD(), params, {
    headers: {
      'x-csrf-token': window.Bynder?.csrf_token ?? csrf,
      'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      'x-requested-with': 'XMLHttpRequest',
    },
  });
};

const getAllAssetIdsFromFeedsCollection = (
  data: NormalizedFeedCollectionResponse,
) => data.collectionPreviews.map(asset => asset.assetId);

export const getCollectionFeedApi = (
  id: string,
): Promise<NormalizedFeedCollectionResponse> => {
  const normalizeFeedsResponse = (collection: CollectionFeedResponse) => {
    return {
      collectionId: collection.id,
      collectionName: collection.name,
      collectionUrl: '',
      collectionPublicUrl: '',
      collectionAssetCount: collection.media.length,
      collectionPreviews: collection.media.map(asset => ({
        isLimited: asset.isLimited,
        isPrivate: asset.isPrivate,
        isArchived: asset.isArchived,
        assetId: asset.id,
        assetName: asset.name,
        assetImageUrl: clearAssetLibraryDefaultIcon(asset.image),
        assetDetailUrl: API_ROUTES.ASSET_OPEN(asset.id),
        assetExtension:
          asset.mediaItems
            .find(mediaItem => mediaItem.type === 'original')
            .extension.toLowerCase() || 'unknown',
      })),
    };
  };

  return axios
    .get<CollectionFeedResponse>(API_ROUTES.COLLECTION_FEED(id))
    .then(res => {
      return normalizeFeedsResponse(res.data);
    });
};

const clearAssetLibraryDefaultIcon = (url: string) => {
  if (
    !url ||
    url.includes('common/no-image.png') ||
    url.includes('medium/rar.jpg') ||
    url.includes('medium/zip.jpg')
  ) {
    return null;
  }

  return url;
};
